// src/redux/api/faqs.api.ts

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  Timestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { 
  EventFaqs, 
  FaqSection,
  FaqItem,
  UsefulLink,
  ContactInfo
} from 'src/@types/faq';

export const FaqsApi = createApi({
  reducerPath: 'faqsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Faqs'],
  endpoints: (builder) => ({
    fetchFaqsByEventId: builder.query<EventFaqs | null, string>({
      queryFn: async (eventId) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            return { data: faqDocSnap.data() as EventFaqs };
          } else {
            return { data: null };
          }
        } catch (error) {
          return { error: error.message };
        }
      },
      providesTags: (result, error, eventId) => 
        result ? [{ type: 'Faqs', id: eventId }] : ['Faqs']
    }),

    createUpdateFaqs: builder.mutation<{ success: boolean, data?: EventFaqs }, EventFaqs>({
      queryFn: async (faqData) => {
        try {
          const { eventId } = faqData;
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          
          // Add timestamps
          const dataWithTimestamps = {
            ...faqData,
            updatedAt: new Date().toISOString(),
          };
          
          await setDoc(faqDocRef, dataWithTimestamps);
          
          return { 
            data: { 
              success: true,
              data: dataWithTimestamps 
            } 
          };
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    // Section Operations
    addFaqSection: builder.mutation<
      { success: boolean, data?: EventFaqs }, 
      { eventId: string, section: FaqSection }
    >({
      queryFn: async ({ eventId, section }) => {
        try {
            console.log("Adding FAQ section:", section);
            console.log("Event ID:", eventId);
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          let updatedFaqs: EventFaqs;
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedSections = [...faqData.sections, section];
            
            updatedFaqs = {
              ...faqData,
              sections: updatedSections,
              updatedAt: new Date().toISOString()
            };
            
            await updateDoc(faqDocRef, {
              sections: updatedSections,
              updatedAt: new Date().toISOString()
            });
          } else {
            // Create new FAQ document if none exists
            updatedFaqs = {
              eventId,
              sections: [section],
              usefulLinks: [],
              contactInfo: [],
              updatedAt: new Date().toISOString(),
              updatedBy: "",  // Will be set in the component
            };
            
            await setDoc(faqDocRef, updatedFaqs);
          }
          
          return { 
            data: { 
              success: true,
              data: updatedFaqs
            } 
          };
        } catch (error) {
            console.error("Error adding FAQ section:", error);
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    updateFaqSection: builder.mutation<
      { success: boolean }, 
      { eventId: string, sectionId: string, updates: Partial<FaqSection> }
    >({
      queryFn: async ({ eventId, sectionId, updates }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const sectionIndex = faqData.sections.findIndex((section: FaqSection) => section.id === sectionId);
            
            if (sectionIndex !== -1) {
              const updatedSections = [...faqData.sections];
              updatedSections[sectionIndex] = {
                ...updatedSections[sectionIndex],
                ...updates,
              };
              
              await updateDoc(faqDocRef, {
                sections: updatedSections,
                updatedAt: new Date().toISOString(),
              });
              
              return { data: { success: true } };
            }
            
            throw new Error("Section not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    deleteFaqSection: builder.mutation<
      { success: boolean }, 
      { eventId: string, sectionId: string }
    >({
      queryFn: async ({ eventId, sectionId }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedSections = faqData.sections.filter((section: FaqSection) => section.id !== sectionId);
            
            await updateDoc(faqDocRef, {
              sections: updatedSections,
              updatedAt: new Date().toISOString(),
            });
            
            return { data: { success: true } };
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    // FAQ Item Operations
    addFaqItem: builder.mutation<
      { success: boolean }, 
      { eventId: string, sectionId: string, item: FaqItem }
    >({
      queryFn: async ({ eventId, sectionId, item }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const sectionIndex = faqData.sections.findIndex((section: FaqSection) => section.id === sectionId);
            
            if (sectionIndex !== -1) {
              const updatedSections = [...faqData.sections];
              updatedSections[sectionIndex] = {
                ...updatedSections[sectionIndex],
                items: [...updatedSections[sectionIndex].items, item],
              };
              
              await updateDoc(faqDocRef, {
                sections: updatedSections,
                updatedAt: new Date().toISOString(),
              });
              
              return { data: { success: true } };
            }
            
            throw new Error("Section not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    updateFaqItem: builder.mutation<
      { success: boolean }, 
      { eventId: string, sectionId: string, itemId: string, updates: Partial<FaqItem> }
    >({
      queryFn: async ({ eventId, sectionId, itemId, updates }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const sectionIndex = faqData.sections.findIndex((section: FaqSection) => section.id === sectionId);
            
            if (sectionIndex !== -1) {
              const itemIndex = faqData.sections[sectionIndex].items.findIndex((item: FaqItem) => item.id === itemId);
              
              if (itemIndex !== -1) {
                const updatedSections = [...faqData.sections];
                updatedSections[sectionIndex] = {
                  ...updatedSections[sectionIndex],
                  items: [...updatedSections[sectionIndex].items],
                };
                
                updatedSections[sectionIndex].items[itemIndex] = {
                  ...updatedSections[sectionIndex].items[itemIndex],
                  ...updates,
                };
                
                await updateDoc(faqDocRef, {
                  sections: updatedSections,
                  updatedAt: new Date().toISOString(),
                });
                
                return { data: { success: true } };
              }
              
              throw new Error("Item not found");
            }
            
            throw new Error("Section not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    deleteFaqItem: builder.mutation<
      { success: boolean }, 
      { eventId: string, sectionId: string, itemId: string }
    >({
      queryFn: async ({ eventId, sectionId, itemId }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const sectionIndex = faqData.sections.findIndex((section: FaqSection) => section.id === sectionId);
            
            if (sectionIndex !== -1) {
              const updatedSections = [...faqData.sections];
              updatedSections[sectionIndex] = {
                ...updatedSections[sectionIndex],
                items: updatedSections[sectionIndex].items.filter((item: FaqItem) => item.id !== itemId),
              };
              
              await updateDoc(faqDocRef, {
                sections: updatedSections,
                updatedAt: new Date().toISOString(),
              });
              
              return { data: { success: true } };
            }
            
            throw new Error("Section not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    // Useful Links Operations
    addUsefulLink: builder.mutation<
      { success: boolean }, 
      { eventId: string, link: UsefulLink }
    >({
      queryFn: async ({ eventId, link }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedLinks = [...faqData.usefulLinks, link];
            
            await updateDoc(faqDocRef, {
              usefulLinks: updatedLinks,
              updatedAt: new Date().toISOString(),
            });
            
            return { data: { success: true } };
          } else {
            // Create new FAQ document if none exists
            const newFaqData: EventFaqs = {
              eventId,
              sections: [],
              usefulLinks: [link],
              contactInfo: [],
              updatedAt: new Date().toISOString(),
              updatedBy: "",  // Will be set in the component
            };
            
            await setDoc(faqDocRef, newFaqData);
            
            return { data: { success: true } };
          }
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    updateUsefulLink: builder.mutation<
      { success: boolean }, 
      { eventId: string, linkId: string, updates: Partial<UsefulLink> }
    >({
      queryFn: async ({ eventId, linkId, updates }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const linkIndex = faqData.usefulLinks.findIndex((link: UsefulLink) => link.id === linkId);
            
            if (linkIndex !== -1) {
              const updatedLinks = [...faqData.usefulLinks];
              updatedLinks[linkIndex] = {
                ...updatedLinks[linkIndex],
                ...updates,
              };
              
              await updateDoc(faqDocRef, {
                usefulLinks: updatedLinks,
                updatedAt: new Date().toISOString(),
              });
              
              return { data: { success: true } };
            }
            
            throw new Error("Link not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    deleteUsefulLink: builder.mutation<
      { success: boolean }, 
      { eventId: string, linkId: string }
    >({
      queryFn: async ({ eventId, linkId }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedLinks = faqData.usefulLinks.filter((link: UsefulLink) => link.id !== linkId);
            
            await updateDoc(faqDocRef, {
              usefulLinks: updatedLinks,
              updatedAt: new Date().toISOString(),
            });
            
            return { data: { success: true } };
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    // Contact Info Operations
    addContactInfo: builder.mutation<
      { success: boolean }, 
      { eventId: string, contact: ContactInfo }
    >({
      queryFn: async ({ eventId, contact }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedContacts = [...faqData.contactInfo, contact];
            
            await updateDoc(faqDocRef, {
              contactInfo: updatedContacts,
              updatedAt: new Date().toISOString(),
            });
            
            return { data: { success: true } };
          } else {
            // Create new FAQ document if none exists
            const newFaqData: EventFaqs = {
              eventId,
              sections: [],
              usefulLinks: [],
              contactInfo: [contact],
              updatedAt: new Date().toISOString(),
              updatedBy: "",  // Will be set in the component
            };
            
            await setDoc(faqDocRef, newFaqData);
            
            return { data: { success: true } };
          }
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    updateContactInfo: builder.mutation<
      { success: boolean }, 
      { eventId: string, contactId: string, updates: Partial<ContactInfo> }
    >({
      queryFn: async ({ eventId, contactId, updates }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const contactIndex = faqData.contactInfo.findIndex((contact: ContactInfo) => contact.id === contactId);
            
            if (contactIndex !== -1) {
              const updatedContacts = [...faqData.contactInfo];
              updatedContacts[contactIndex] = {
                ...updatedContacts[contactIndex],
                ...updates,
              };
              
              await updateDoc(faqDocRef, {
                contactInfo: updatedContacts,
                updatedAt: new Date().toISOString(),
              });
              
              return { data: { success: true } };
            }
            
            throw new Error("Contact not found");
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),

    deleteContactInfo: builder.mutation<
      { success: boolean }, 
      { eventId: string, contactId: string }
    >({
      queryFn: async ({ eventId, contactId }) => {
        try {
          const faqDocRef = doc(DB, "events", eventId, "faqs", "eventFaqs");
          const faqDocSnap = await getDoc(faqDocRef);
          
          if (faqDocSnap.exists()) {
            const faqData = faqDocSnap.data() as EventFaqs;
            const updatedContacts = faqData.contactInfo.filter((contact: ContactInfo) => contact.id !== contactId);
            
            await updateDoc(faqDocRef, {
              contactInfo: updatedContacts,
              updatedAt: new Date().toISOString(),
            });
            
            return { data: { success: true } };
          }
          
          throw new Error("FAQ document not found");
        } catch (error) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, { eventId }) => 
        [{ type: 'Faqs', id: eventId }]
    }),
  })
});

export const {
  useFetchFaqsByEventIdQuery,
  useCreateUpdateFaqsMutation,
  useAddFaqSectionMutation,
  useUpdateFaqSectionMutation,
  useDeleteFaqSectionMutation,
  useAddFaqItemMutation,
  useUpdateFaqItemMutation,
  useDeleteFaqItemMutation,
  useAddUsefulLinkMutation,
  useUpdateUsefulLinkMutation,
  useDeleteUsefulLinkMutation,
  useAddContactInfoMutation,
  useUpdateContactInfoMutation,
  useDeleteContactInfoMutation
} = FaqsApi;